import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import chatService from "../../_services/cart/chat.service";
import wsService from "../../_services/cart/ws.service";
import authService from "../../_services/cart/authService";
import {history} from "../../_helpers";

/**
 * El centro de mensajes: la lista de conversaciones a la izquierda y el hilo abierto a la derecha.
 *
 * <h3>Un solo componente para los dos lados</h3>
 * No hay una pantalla del comprador y otra del vendedor. En DeAutopartes la misma persona compra en
 * un hilo y vende en el otro, así que lo único que cambia es de qué lado se pinta la burbuja, y eso
 * lo dice el backend hilo por hilo (`lado`). Dos pantallas serían dos lugares donde arreglar el
 * mismo bug de scroll.
 *
 * <h3>En mobile es una pantalla por vez</h3>
 * Los dos paneles no entran en un teléfono, y achicarlos deja una lista ilegible al lado de un chat
 * ilegible. Con una conversación abierta, la lista se oculta y aparece la flecha de volver; en
 * desktop conviven, que es lo que permite saltar de un hilo a otro sin perder el contexto.
 */

const TIPOS = [
    {id: "", label: "Todos", icono: "fa-solid fa-inbox"},
    {id: "CONSULTA", label: "Consultas", icono: "fa-regular fa-comments"},
    {id: "PEDIDO", label: "Compras", icono: "fa-solid fa-box"},
];

/** La hora si es de hoy, el día si no: en una lista de chats la fecha completa es ruido. */
const formatearFecha = (iso, largo = false) => {
    if (!iso) return "";
    const fecha = new Date(iso);
    const hoy = new Date();
    const mismoDia = fecha.toDateString() === hoy.toDateString();

    if (mismoDia && !largo) {
        return fecha.toLocaleTimeString("es-AR", {hour: "2-digit", minute: "2-digit"});
    }
    if (largo) {
        return fecha.toLocaleString("es-AR", {
            day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
        });
    }
    return fecha.toLocaleDateString("es-AR", {day: "2-digit", month: "2-digit"});
};

const tituloDelHilo = (hilo, yoSoyVendedor) => {
    const otro = yoSoyVendedor ? hilo.compradorUsername : hilo.vendedorUsername;
    return otro || "Conversación";
};

/** Qué se está hablando, para que el vendedor entienda la fila sin abrirla. */
const referenciaDelHilo = (hilo) => {
    if (hilo.tipo === "PEDIDO") return `Compra #${hilo.ventaId}`;
    const partes = [hilo.productoMarca, hilo.productoModelo, hilo.productoRepuesto].filter(Boolean);
    return partes.length ? partes.join(" · ") : "Consulta general";
};

export const Mensajes = () => {

    const params = new URLSearchParams(history.location.search);

    const [tipo, setTipo] = useState(params.get("tipo") || "");
    const [busqueda, setBusqueda] = useState("");
    const [soloNoLeidas, setSoloNoLeidas] = useState(false);
    const [hilos, setHilos] = useState([]);
    const [cargandoBandeja, setCargandoBandeja] = useState(true);

    const [abiertoId, setAbiertoId] = useState(
        params.get("conversacion") ? Number(params.get("conversacion")) : null
    );
    const [cabecera, setCabecera] = useState(null);
    const [mensajes, setMensajes] = useState([]);
    const [cargandoHilo, setCargandoHilo] = useState(false);

    const [borrador, setBorrador] = useState("");
    const [enviando, setEnviando] = useState(false);

    const finDelHilo = useRef(null);
    const textarea = useRef(null);

    /**
     * Quién soy, para saber de qué lado estoy en cada hilo.
     *
     * <p>Lo devuelve la propia bandeja en vez de leerlo del usuario guardado en localStorage: ese
     * `detail` se llena con una llamada aparte al arrancar la app y puede no estar todavía cuando
     * esta pantalla monta —entrando directo por el deep link de un push, por ejemplo—, y sin el id
     * todas las burbujas caen del mismo lado.
     */
    const [yoId, setYoId] = useState(authService.getLoggedUser()?.detail?.id);

    // ─────────── Bandeja ───────────

    const cargarBandeja = useCallback(() => {
        setCargandoBandeja(true);
        chatService
            .bandeja({tipo, q: busqueda, soloNoLeidas})
            .then((data) => {
                setHilos(data?.conversaciones || []);
                if (data?.userId) setYoId(data.userId);
                setCargandoBandeja(false);
            })
            .catch(() => setCargandoBandeja(false));
    }, [tipo, busqueda, soloNoLeidas]);

    useEffect(() => {
        cargarBandeja();
    }, [cargarBandeja]);

    useEffect(() => {
        chatService.refrescarNoLeidos();
    }, []);

    // ─────────── Hilo abierto ───────────

    const abrirHilo = useCallback((id) => {
        setAbiertoId(id);
        setCargandoHilo(true);
        setMensajes([]);
        chatService
            .hilo(id)
            .then((data) => {
                setCabecera(data?.conversacion || null);
                setMensajes(data?.mensajes || []);
                setCargandoHilo(false);
                // Abrir el hilo lo marca leído en el backend: el globo de esta fila ya no va.
                setHilos((prev) =>
                    prev.map((h) =>
                        h.id === id ? {...h, noLeidosComprador: 0, noLeidosVendedor: 0} : h
                    )
                );
                chatService.refrescarNoLeidos();
            })
            .catch(() => setCargandoHilo(false));
    }, []);

    useEffect(() => {
        if (abiertoId) abrirHilo(abiertoId);
        // Sólo al montar y cuando cambia el id pedido por la URL: abrirHilo ya es estable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abiertoId]);

    /** El scroll al pie del hilo. Un chat que abre arriba de todo obliga a scrollear para leer. */
    useEffect(() => {
        finDelHilo.current?.scrollIntoView({behavior: mensajes.length > 30 ? "auto" : "smooth"});
    }, [mensajes]);

    // ─────────── Tiempo real ───────────

    /**
     * Dos suscripciones distintas y las dos hacen falta.
     *
     * El topic del hilo trae el mensaje a la conversación que está abierta; el evento global
     * `chat-mensaje` —que dispara el servicio de notificaciones desde la cola personal— es el que
     * mueve el hilo al tope de la bandeja cuando el mensaje llegó a OTRA conversación. Con sólo el
     * primero, la lista quedaría desactualizada hasta recargar.
     */
    useEffect(() => {
        if (!abiertoId) return undefined;

        return wsService.subscribe(`/topic/chat/${abiertoId}`, (msg) => {
            setMensajes((prev) => {
                // El propio mensaje ya se agregó al responder el POST: sin esto se ve duplicado.
                if (prev.some((m) => m.id === msg.id)) return prev;
                return [...prev, {
                    id: msg.id,
                    emisor: msg.emisor,
                    texto: msg.texto,
                    leido: false,
                    createdInstant: msg.createdInstant,
                    enviadoPorUserId: msg.enviadoPorUserId,
                }];
            });
        });
    }, [abiertoId]);

    useEffect(() => {
        const alLlegarMensaje = (ev) => {
            const msg = ev.detail || {};
            setHilos((prev) => {
                const i = prev.findIndex((h) => h.id === msg.conversacionId);
                if (i < 0) {
                    // Es un hilo que esta bandeja no tiene todavía (recién abierto por el otro lado).
                    cargarBandeja();
                    return prev;
                }
                const hilo = prev[i];
                const esElAbierto = hilo.id === abiertoId;
                const actualizado = {
                    ...hilo,
                    ultimoMensajeTexto: msg.texto,
                    ultimoMensajeAt: msg.createdInstant,
                    ultimoMensajeEmisor: msg.emisor,
                    // El hilo que se está mirando no suma no leídos: se está leyendo.
                    noLeidosComprador: esElAbierto ? 0 : (hilo.noLeidosComprador || 0) + (msg.emisor === "VENDEDOR" ? 1 : 0),
                    noLeidosVendedor: esElAbierto ? 0 : (hilo.noLeidosVendedor || 0) + (msg.emisor === "COMPRADOR" ? 1 : 0),
                };
                return [actualizado, ...prev.filter((_, idx) => idx !== i)];
            });
        };

        window.addEventListener("chat-mensaje", alLlegarMensaje);
        return () => window.removeEventListener("chat-mensaje", alLlegarMensaje);
    }, [abiertoId, cargarBandeja]);

    // ─────────── Envío ───────────

    const enviar = (e) => {
        if (e) e.preventDefault();
        const texto = borrador.trim();
        if (!texto || !abiertoId || enviando) return;

        setEnviando(true);
        chatService
            .enviar(abiertoId, texto)
            .then((data) => {
                const nuevo = data?.mensaje;
                if (nuevo) {
                    setMensajes((prev) =>
                        prev.some((m) => m.id === nuevo.id) ? prev : [...prev, nuevo]
                    );
                    setHilos((prev) =>
                        prev.map((h) =>
                            h.id === abiertoId
                                ? {
                                    ...h,
                                    ultimoMensajeTexto: nuevo.texto,
                                    ultimoMensajeAt: nuevo.createdInstant,
                                    ultimoMensajeEmisor: nuevo.emisor,
                                }
                                : h
                        )
                    );
                }
                setBorrador("");
                setEnviando(false);
                if (textarea.current) textarea.current.style.height = "auto";
            })
            .catch(() => setEnviando(false));
    };

    /** Enter manda, Shift+Enter hace salto de línea. En mobile el Enter del teclado es "enviar". */
    const alTeclear = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            enviar();
        }
    };

    /** El textarea crece con el texto hasta un tope, como cualquier chat. */
    const alEscribir = (e) => {
        setBorrador(e.target.value);
        const el = e.target;
        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight, 120) + "px";
    };

    const hiloAbierto = useMemo(
        () => hilos.find((h) => h.id === abiertoId),
        [hilos, abiertoId]
    );

    const soyVendedorEn = useCallback(
        (hilo) => !!yoId && hilo.vendedorId === yoId,
        [yoId]
    );

    const noLeidosDe = useCallback(
        (hilo) => (soyVendedorEn(hilo) ? hilo.noLeidosVendedor : hilo.noLeidosComprador) || 0,
        [soyVendedorEn]
    );

    const volver = () => {
        setAbiertoId(null);
        setCabecera(null);
        setMensajes([]);
    };

    const esMio = (m) =>
        cabecera ? m.emisor === cabecera.lado : m.enviadoPorUserId === yoId;

    return (
        <div className="chat">
            <div className="chat__header">
                <h1 className="chat__header-titulo">Mensajes</h1>
                <p className="chat__header-bajada">
                    Consultá por una publicación o hablá con el vendedor sobre una compra.
                </p>
            </div>

            <div className={`chat__panel ${abiertoId ? "chat__panel--conHilo" : ""}`}>

                {/* ══════════ Bandeja ══════════ */}
                <aside className="chat__bandeja">

                    <div className="chat__filtros">
                        <div className="chat__tabs" role="tablist">
                            {TIPOS.map((t) => (
                                <button
                                    key={t.id || "todos"}
                                    type="button"
                                    role="tab"
                                    aria-selected={tipo === t.id}
                                    className={`chat__tab ${tipo === t.id ? "chat__tab--activa" : ""}`}
                                    onClick={() => setTipo(t.id)}
                                >
                                    <FontAwesomeIcon icon={t.icono}/>
                                    <span>{t.label}</span>
                                </button>
                            ))}
                        </div>

                        <div className="chat__buscador">
                            <FontAwesomeIcon icon="fa-solid fa-magnifying-glass"/>
                            <input
                                type="search"
                                value={busqueda}
                                placeholder="Buscar por nombre o mensaje"
                                onChange={(e) => setBusqueda(e.target.value)}
                            />
                        </div>

                        <button
                            type="button"
                            className={`chat__filtroNoLeidas ${soloNoLeidas ? "chat__filtroNoLeidas--activo" : ""}`}
                            onClick={() => setSoloNoLeidas(!soloNoLeidas)}
                        >
                            <FontAwesomeIcon icon={soloNoLeidas ? "fa-solid fa-circle-check" : "fa-regular fa-circle"}/>
                            Solo sin leer
                        </button>
                    </div>

                    <div className="chat__lista">
                        {cargandoBandeja && <p className="chat__estado">Cargando…</p>}

                        {!cargandoBandeja && hilos.length === 0 && (
                            <div className="chat__vacio">
                                <FontAwesomeIcon icon="fa-regular fa-comments" size="2x"/>
                                <p>
                                    {soloNoLeidas
                                        ? "No tenés mensajes sin leer."
                                        : "Todavía no tenés conversaciones."}
                                </p>
                                <span>
                                    Entrá a una publicación y tocá <strong>Consultar al vendedor</strong> para
                                    empezar.
                                </span>
                            </div>
                        )}

                        {hilos.map((h) => {
                            const sinLeer = noLeidosDe(h);
                            const ultimoEsMio =
                                h.ultimoMensajeEmisor === (soyVendedorEn(h) ? "VENDEDOR" : "COMPRADOR");
                            return (
                                <button
                                    key={h.id}
                                    type="button"
                                    className={`chat__item ${h.id === abiertoId ? "chat__item--activo" : ""} ${sinLeer ? "chat__item--sinLeer" : ""}`}
                                    onClick={() => abrirHilo(h.id)}
                                >
                                    <span className="chat__avatar" aria-hidden="true">
                                        {tituloDelHilo(h, soyVendedorEn(h)).substring(0, 2).toUpperCase()}
                                    </span>

                                    <span className="chat__itemCuerpo">
                                        <span className="chat__itemFila">
                                            <span className="chat__itemNombre">
                                                {tituloDelHilo(h, soyVendedorEn(h))}
                                            </span>
                                            <span className="chat__itemFecha">
                                                {formatearFecha(h.ultimoMensajeAt)}
                                            </span>
                                        </span>

                                        <span className="chat__itemRef">
                                            <FontAwesomeIcon
                                                icon={h.tipo === "PEDIDO" ? "fa-solid fa-box" : "fa-solid fa-tag"}
                                            />
                                            {referenciaDelHilo(h)}
                                        </span>

                                        <span className="chat__itemFila">
                                            <span className="chat__itemPreview">
                                                {ultimoEsMio && <em>Vos: </em>}
                                                {h.ultimoMensajeTexto || "Sin mensajes todavía"}
                                            </span>
                                            {sinLeer > 0 && (
                                                <span className="chat__globo">{sinLeer > 9 ? "9+" : sinLeer}</span>
                                            )}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                {/* ══════════ Hilo ══════════ */}
                <section className="chat__hilo">

                    {!abiertoId && (
                        <div className="chat__hiloVacio">
                            <FontAwesomeIcon icon="fa-regular fa-comment-dots" size="3x"/>
                            <p>Elegí una conversación para ver los mensajes.</p>
                        </div>
                    )}

                    {abiertoId && (
                        <>
                            <div className="chat__hiloHeader">
                                <button
                                    type="button"
                                    className="chat__volver"
                                    onClick={volver}
                                    aria-label="Volver a la lista"
                                >
                                    <FontAwesomeIcon icon="fa-solid fa-arrow-left"/>
                                </button>

                                <span className="chat__avatar chat__avatar--header" aria-hidden="true">
                                    {(cabecera?.otroUsername || "?").substring(0, 2).toUpperCase()}
                                </span>

                                <div className="chat__hiloTitulo">
                                    <strong>{cabecera?.otroUsername || "Conversación"}</strong>
                                    <span>
                                        {hiloAbierto ? referenciaDelHilo(hiloAbierto) : ""}
                                        {cabecera?.estado === "CERRADA" && " · Cerrada"}
                                    </span>
                                </div>

                                {cabecera?.productoId && (
                                    <button
                                        type="button"
                                        className="chat__accion"
                                        title="Ver la publicación"
                                        onClick={() => history.push(`/productDetail?id=${cabecera.productoId}`)}
                                    >
                                        <FontAwesomeIcon icon="fa-solid fa-up-right-from-square"/>
                                    </button>
                                )}
                            </div>

                            <div className="chat__mensajes">
                                {cargandoHilo && <p className="chat__estado">Cargando…</p>}

                                {!cargandoHilo && mensajes.length === 0 && (
                                    <p className="chat__estado chat__estado--centrado">
                                        Escribí tu consulta y te van a responder por acá.
                                    </p>
                                )}

                                {mensajes.map((m) => (
                                    <div
                                        key={m.id}
                                        className={`chat__burbujaFila ${esMio(m) ? "chat__burbujaFila--mia" : ""}`}
                                    >
                                        <div className={`chat__burbuja ${esMio(m) ? "chat__burbuja--mia" : ""}`}>
                                            <p>{m.texto}</p>
                                            <span className="chat__burbujaMeta">
                                                {formatearFecha(m.createdInstant, true)}
                                                {esMio(m) && (
                                                    <FontAwesomeIcon
                                                        icon={m.leido ? "fa-solid fa-check-double" : "fa-solid fa-check"}
                                                        className={m.leido ? "chat__visto" : ""}
                                                    />
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                ))}

                                <div ref={finDelHilo}/>
                            </div>

                            <form className="chat__escribir" onSubmit={enviar}>
                                <textarea
                                    ref={textarea}
                                    rows={1}
                                    value={borrador}
                                    onChange={alEscribir}
                                    onKeyDown={alTeclear}
                                    placeholder="Escribí tu mensaje…"
                                />
                                <button
                                    type="submit"
                                    className="chat__enviar"
                                    disabled={!borrador.trim() || enviando}
                                    aria-label="Enviar"
                                >
                                    <FontAwesomeIcon icon="fa-solid fa-paper-plane"/>
                                </button>
                            </form>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};
